import { runReadModifyWrite, runReadSnapshot, runWriteBundle } from "./_bundle";
import { IdbConnection } from "./_connection";
import { classifyIdbError, degradeScopeFor, isReportableFailure } from "./_errors";
import { requestDone } from "./_request";
import { SerialTaskQueue } from "./_writeQueue";
import type {
  IdbDatabaseSchema,
  IdbFailure,
  IdbFailureReporter,
  IdbReadModifyWrite,
  IdbReadOp,
  IdbWriteOp,
  IdbWriteResult,
} from "./_types";

/**
 * IndexedDB 캐시 커널의 유일한 외부 표면(API).
 *
 * Owns:
 * - 연결 수명, 쓰기 직렬화, 원자 커밋을 하나로 묶은 진입점.
 * - 실패 상태(degrade)를 얼마나 오래 유지할지.
 *
 * Does not own:
 * - 어떤 스토어가 있는지, 레코드가 무슨 뜻인지. 스키마는 주입된다.
 * - 캐시 적중 판정, 세대 정책, 용량 회수 정책. 소비처가 소유한다.
 * - 실패를 어디에 기록할지. `onFailure` 로 주입된다.
 *
 * **`IDBDatabase` 핸들은 여기서 내보내지 않는다.** 핸들을 전달하면 호출부가 자기 트랜잭션을
 * 열 수 있다. 그러면 쓰기 직렬화와 원자성을 모두 우회한다. 트랜잭션 안에서 네트워크를 기다리는
 * 사고도 그곳에서 생긴다.
 *
 * **어떤 메서드도 거절(reject)하지 않는다.** 캐시가 동작하지 않으면 기능 실패가 아니라 캐시 미스여야
 * 한다. 호출부에 try/catch 를 강제하지 않으면, 이 성질을 규약이 아니라 구조로 보장한다.
 */

export type {
  IdbDatabaseSchema,
  IdbFailure,
  IdbFailureKind,
  IdbFailureReporter,
  IdbReadModifyWrite,
  IdbReadOp,
  IdbStoreSchema,
  IdbWriteOp,
  IdbWriteResult,
} from "./_types";

// 다른 탭이 구버전 연결을 유지할 때 기다림을 멈추는 시점. 로컬 저장소이므로 넉넉하게 둔다.
// 이 시간 동안 소비처는 네트워크(왕복 250ms)를 쓰므로 사용자가 느끼는 손해가 없다.
const DEFAULT_TIMEOUT_MS = 5_000;

export type IdbCacheOptions = {
  schema: IdbDatabaseSchema;
  // 지원하지 않는 환경은 null. 테스트는 여기로 구현을 주입한다. 전역 폴리필은 설치하지 않는다.
  factory: IDBFactory | null;
  onFailure: IdbFailureReporter;
  timeoutMs?: number;
};

/** 저장된 값을 좁히는 검증자. 형태가 맞지 않으면 null. 저장소 값은 외부 입력으로 취급한다. */
export type IdbValueAcceptor<T> = (raw: unknown) => T | null;

export class IdbCache {
  private readonly connection: IdbConnection;
  private readonly writes = new SerialTaskQueue();
  private readonly onFailure: IdbFailureReporter;
  // degrade 는 유지된다. 한 번 닫으면 다음 init()(새로고침, 재로그인)까지 다시 시도하지 않는다.
  // 다시 시도하면 화면마다 같은 실패를 반복하고, 매번 대량 복제 비용도 쓴다.
  private degradedBy: IdbFailure | null = null;
  private writesOnly = false;
  private active = false;

  constructor(options: IdbCacheOptions) {
    this.onFailure = options.onFailure;
    this.connection = new IdbConnection({
      schema: options.schema,
      factory: options.factory,
      timeoutMs: options.timeoutMs ?? DEFAULT_TIMEOUT_MS,
    });
  }

  /** 세션을 연다. 연결은 첫 사용까지 미룬다. 반복 호출에 안전하다. */
  init(): void {
    if (this.active) {
      return;
    }
    this.active = true;
    this.clearDegrade();
    this.connection.init();
  }

  /** **동기 함수다.** `pagehide` 핸들러가 호출한다. 대기 중인 쓰기는 다음 tick 에 중단으로 끝난다. */
  dispose(): void {
    this.active = false;
    this.connection.dispose();
  }

  /** 한 논리 연산을 한 트랜잭션으로 커밋한다. 전부 커밋되거나 전부 롤백된다. */
  async write(ops: readonly IdbWriteOp[]): Promise<IdbWriteResult> {
    if (this.degradedBy !== null) {
      // 닫힌 저장소에는 복제 비용도 쓰지 않는다. 용량 초과(쓰기만 닫힘)도 여기서 막는다.
      return { ok: false, failure: this.degradedBy };
    }
    const generation = this.connection.currentGeneration();
    return this.writes.run(async () => {
      // 대기하는 동안 앞 작업이 용량 초과로 쓰기를 닫았을 수 있다.
      if (this.degradedBy !== null) {
        return { ok: false, failure: this.degradedBy };
      }
      const opened = await this.openFor(generation);
      if (!opened.ok) {
        return { ok: false, failure: opened.failure };
      }
      try {
        await runWriteBundle(opened.db, ops);
        return { ok: true };
      } catch (error) {
        return { ok: false, failure: this.record(classifyIdbError(error)) };
      }
    });
  }

  /**
   * 여러 값을 같은 readonly 트랜잭션에서 읽는다. 결과는 조회 순서의 외부 입력이다.
   * 읽기 실패는 null 이며, 값의 검증과 관련 레코드 정리는 소비처가 결정한다.
   */
  async readSnapshot(reads: readonly IdbReadOp[]): Promise<readonly unknown[] | null> {
    const opened = await this.openFor(this.connection.currentGeneration());
    if (!opened.ok) return null;
    try {
      return await runReadSnapshot(opened.db, reads);
    } catch (error) {
      this.record(classifyIdbError(error));
      return null;
    }
  }

  /**
   * 최신 값 조회, 동기 판단, 커밋을 한 작업으로 직렬화한다.
   * 같은 스토어의 readwrite 잠금이 독립 창·탭의 연결까지 보호한다. 네트워크 요청은 호출 전에 끝낸다.
   */
  async readModifyWrite(work: IdbReadModifyWrite): Promise<IdbWriteResult> {
    const generation = this.connection.currentGeneration();
    return this.writes.run(async () => {
      if (this.degradedBy !== null) return { ok: false, failure: this.degradedBy };
      const opened = await this.openFor(generation);
      if (!opened.ok) return { ok: false, failure: opened.failure };
      try {
        await runReadModifyWrite(opened.db, work);
        return { ok: true };
      } catch (error) {
        return { ok: false, failure: this.record(classifyIdbError(error)) };
      }
    });
  }

  /**
   * 키 하나를 읽는다. 값이 없거나, 읽지 못했거나, 형태가 맞지 않으면 `null` 이다. 셋을 구분하지 않는다.
   * 어느 경우든 소비처는 원본을 받아 오기만 하면 된다.
   *
   * 읽기는 쓰기 대기열에 넣지 않는다. 같은 스토어의 순서는 IDB 락이 이미 보장한다. 대기열에
   * 넣으면 무관한 스토어의 조회도 대량 쓰기가 끝날 때까지 기다린다.
   */
  async read<T>(store: string, key: IDBValidKey, accept: IdbValueAcceptor<T>): Promise<T | null> {
    const generation = this.connection.currentGeneration();
    const opened = await this.openFor(generation);
    if (!opened.ok) {
      return null;
    }
    let raw: unknown;
    try {
      raw = await requestDone(opened.db.transaction(store).objectStore(store).get(key));
    } catch (error) {
      this.record(classifyIdbError(error));
      return null;
    }
    if (raw === undefined) {
      return null;
    }
    const value = accept(raw);
    if (value === null) {
      // 거절한 레코드를 남기면 진입할 때마다 같은 잘못된 값을 다시 읽는다. 삭제 결과는 기다리지 않는다.
      void this.write([{ kind: "delete", store, keys: [key] }]);
    }
    return value;
  }

  /**
   * 스토어 전체를 읽는다. 읽지 못했으면 `null`, 비어 있으면 빈 배열이다. 이 둘은 구분한다.
   * 소비처가 "아직 모른다" 와 "없다" 를 같게 다루면, 매번 다시 읽거나 절대 읽지 않는다.
   *
   * **바이트를 담은 스토어에는 쓰지 않는다.** 이 호출은 스토어 전체를 한 번에 메모리에 올린다.
   * Blob 을 담은 스토어에 호출하면 캐시가 아끼려던 메모리를 한 번에 다 쓴다. 회수 판정처럼
   * 전체 레코드가 필요한 용도는 바이트 없는 별도 스토어로 나누고, 그 스토어를 읽는다.
   */
  async readAll<T>(store: string, accept: IdbValueAcceptor<T>): Promise<readonly T[] | null> {
    const generation = this.connection.currentGeneration();
    const opened = await this.openFor(generation);
    if (!opened.ok) {
      return null;
    }
    let raw: unknown[];
    try {
      raw = await requestDone(opened.db.transaction(store).objectStore(store).getAll());
    } catch (error) {
      this.record(classifyIdbError(error));
      return null;
    }
    const values: T[] = [];
    const rejected: unknown[] = [];
    for (const entry of raw) {
      const value = accept(entry);
      if (value === null) {
        rejected.push(entry);
        continue;
      }
      values.push(value);
    }
    if (rejected.length > 0) {
      // 거절한 레코드는 형태가 맞지 않으므로 키를 알 수 없다. 이 스토어는 전체를 다시 채울 수 있다.
      // 그래서 스토어 전체를 비우고 다시 채우게 한다. 잘못된 값을 남기면 진입할 때마다 다시 읽는다.
      void this.write([{ kind: "clear", store }]);
      return null;
    }
    return values;
  }

  /**
   * 데이터베이스 전체를 지운다. 사용자 전환과 수동 비우기가 이 경로를 쓴다.
   *
   * degrade 상태에서도 실행한다. 용량이 차서 닫힌 저장소를 사용자가 복구하는 방법은 이것뿐이다.
   * 성공하면 degrade 도 함께 해제한다.
   */
  async clearAll(): Promise<void> {
    await this.writes.run(async () => {
      const failure = await this.connection.deleteDatabase();
      if (failure === null) {
        this.clearDegrade();
        return;
      }
      this.record(failure);
    });
  }

  private async openFor(
    generation: number,
  ): Promise<{ ok: true; db: IDBDatabase } | { ok: false; failure: IdbFailure }> {
    if (this.degradedBy !== null && !this.writesOnly) {
      return { ok: false, failure: this.degradedBy };
    }
    if (generation !== this.connection.currentGeneration()) {
      return { ok: false, failure: aborted() };
    }
    // 연결 계층은 이미 결과 union 을 돌려준다. 그래도 "이 표면은 거절하지 않는다" 는 이 계층이
    // 보장한다. 아래 계층에서 예외가 전달되면 소비처의 캐시 미스가 기능 실패로 바뀐다.
    let opened;
    try {
      opened = await this.connection.open();
    } catch (error) {
      return { ok: false, failure: this.record(classifyIdbError(error)) };
    }
    if (!opened.ok) {
      return { ok: false, failure: this.record(opened.failure) };
    }
    // 여는 동안 세션이 바뀌었으면, 이 연결로 한 작업은 다음 세션에 속하지 않는다.
    if (generation !== this.connection.currentGeneration()) {
      return { ok: false, failure: aborted() };
    }
    return opened;
  }

  // 실패를 degrade 범위에 반영하고 보고한다. 받은 실패를 그대로 돌려주므로 호출부가 계속 쓸 수 있다.
  private record(failure: IdbFailure): IdbFailure {
    if (isReportableFailure(failure.kind)) {
      this.onFailure(failure.message, failure);
    }
    const scope = degradeScopeFor(failure.kind);
    if (scope === "none") {
      return failure;
    }
    this.degradedBy = failure;
    this.writesOnly = scope === "writes";
    return failure;
  }

  private clearDegrade(): void {
    this.degradedBy = null;
    this.writesOnly = false;
  }
}

function aborted(): IdbFailure {
  return { kind: "aborted", message: "The cache session is closed." };
}
